import React, { useCallback, useState } from 'react';
import {
  Stack,
  styled,
  Typography,
  useTheme,
  OutlinedInputProps,
  TextField as MuiTextField,
  Theme,
} from '@mui/material';
import { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField/TextField';
import { LoadingButton } from '@mui/lab';
import { Formik, Form as FormikForm, FormikHelpers, useField } from 'formik';
import * as Yup from 'yup';

import { Container } from '@/components/layout';
import Icon from '@/components/icons/Icon';
import parseServerError from '@/utils/parseServerError';
import contactUsService from '@/services/contactUsService';

import type { PartnerWithUsValues } from './types';

const BackgroundSection = styled('section')`
  background-image: url(/images/contact-us/partner-with-us-bg.jpg);
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  @media (max-width: 1024px) {
    background-image: unset;
  }
`;

const Content = styled(Stack)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 3.125rem;

  @media (max-width: 1024px) {
    column-gap: 0;
    grid-template-columns: 1fr;
  }
`;

const Form = styled(FormikForm)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.5rem;
  max-width: 100%;

  .form-left,
  .form-right {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .form-right button {
    height: 44px;
    width: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    column-gap: 0rem;
    row-gap: 1rem;
  }
`;

const FloatTextField = styled((props: MuiTextFieldProps) => (
  <MuiTextField
    InputProps={{ disableUnderline: true } as Partial<OutlinedInputProps>}
    variant="filled"
    {...props}
  />
))(
  ({
    theme,
    multiline,
    error,
  }: {
    theme: Theme;
    multiline: boolean;
    error: boolean;
  }) => ({
    '& .MuiInputLabel-root': {
      fontSize: theme.spacing(3.5),
      lineHeight: theme.spacing(4.9),
      color: '#52545f',
      transform: `
      translate(${theme.spacing(6)}, ${theme.spacing(2.5)})
    `,
      '&.MuiFormLabel-filled, &.Mui-focused': {
        color: theme.palette.primary.main,
        transform: `
        translate(${theme.spacing(6)}, ${theme.spacing(1)}) scale(0.75)
      `,
      },
    },
    '& .MuiFilledInput-input': {
      fontSize: theme.spacing(3.5),
      lineHeight: theme.spacing(4.9),
      color: '#52545f',
      height: 'auto',
      padding: theme.spacing(4, 6, 0.5),
      borderRadius: 0,
    },
    '& .MuiFilledInput-root': {
      height: '40px',
      resize: 'none',
      overflow: 'hidden',
      borderRadius: '23px',
      backgroundColor: theme.palette.common.white,
      outline: error ? `1px solid ${theme.palette.error.main}` : 'none',
      transition: theme.transitions.create(['background-color', 'box-shadow']),
      '&.MuiInputBase-multiline': {
        height: '148px',
        alignItems: 'flex-start',
        padding: theme.spacing(5, 6, 0.5),
      },
      '& textarea': {
        padding: 0,
        margin: 0,
      },
      '&:before, &:after': {
        content: 'none',
      },
      '&.Mui-focused, &.Mui-hover': {
        backgroundColor: theme.palette.common.white,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.grey[100],
      },
    },
  }),
) as typeof MuiTextField;

function TextField({ label, name, ...props }: MuiTextFieldProps): JSX.Element {
  const [field, meta] = useField({ name });
  const shouldShowError = !!(meta.touched && meta.error);
  return (
    <>
      <FloatTextField
        label={label}
        error={shouldShowError}
        {...field}
        {...props}
      />
      {/* {shouldShowError && (
        <ErrorMessage css={xw`mt-1 sm:mt-2`}>
          {capitalize(meta.error)}
          {meta.initialError}
        </ErrorMessage>
      )} */}
    </>
  );
}

const INITIAL_PARTNER_WITH_US_FORM_VALUES = {
  name: '',
  job_title: '',
  email: '',
  phone_number: '',
  country: '',
  company_name: '',
  number_of_employees: undefined,
  industry: '',
  message: '',
};

const errors = {
  required: 'This field is required.',
  email: 'Invalid email. Please enter your working email address.',
};

const PartnerWithUsSchema = Yup.object().shape({
  name: Yup.string().trim().required(errors.required),
  job_title: Yup.string().trim().required(errors.required),
  email: Yup.string().trim().email(errors.email).required(errors.required),
  phone_number: Yup.string().trim().required(errors.required),
  country: Yup.string().trim().required(errors.required),
  company_name: Yup.string().trim().required(errors.required),
  number_of_employees: Yup.number().required(errors.required),
  industry: Yup.string().trim(),
  message: Yup.string().trim(),
});

function PartnerWithUsForm() {
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState(null);

  const handleSubmit = useCallback(
    async (
      values: PartnerWithUsValues,
      actions: FormikHelpers<PartnerWithUsValues>,
    ) => {
      try {
        setIsLoading(true);
        if (serverErrors) {
          setServerErrors(null);
        }
        await contactUsService.send(values);

        actions.setSubmitting(false);
        actions.resetForm();
      } catch (err) {
        setServerErrors(parseServerError(err?.response?.data));
      } finally {
        setIsLoading(false);
      }
    },
    [serverErrors],
  );

  return (
    <BackgroundSection
      sx={{
        paddingTop: { xs: theme.spacing(6), md: theme.spacing(12.5) },
        paddingBottom: { xs: theme.spacing(10), md: theme.spacing(15) },
        backgroundColor: { xs: '#D5ECEC', lg: 'unset' },
      }}
    >
      <Container>
        <Content>
          <div />

          <Stack
            sx={{
              gap: { xs: theme.spacing(6), md: theme.spacing(9.5) },
            }}
          >
            <Stack
              sx={{
                gap: { xs: theme.spacing(4), md: theme.spacing(5) },
              }}
            >
              <Typography
                fontSize="3.125rem !important"
                lineHeight="1.18 !important"
                variant="h1"
                color={theme.palette.common.white}
                sx={{
                  color: '#34353E',
                  fontSize: {
                    xs: '2.375rem !important',
                    md: '3.125rem !important',
                  },
                  lineHeight: { xs: '1.2 !important', md: '1.18 !important' },
                }}
              >
                Partner with Us
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#52545F',
                  fontSize: '1.125rem !important',
                  lineHeight: '1.46 !important',
                }}
              >
                Connect with our team to learn how OneVillage can help your
                business or brokerage
              </Typography>
            </Stack>

            <Stack
              sx={{
                borderRadius: theme.spacing(4),
                overflow: { xs: 'unset', lg: 'hidden' },
                backgroundColor: '#D5ECEC',
              }}
            >
              <Stack
                sx={{
                  gap: theme.spacing(4),
                  alignItems: 'center',
                  flexDirection: 'row',
                  backgroundColor: theme.palette.primary.main,

                  padding: theme.spacing(3, 6),
                }}
              >
                <Icon
                  iconName="chat"
                  style={{
                    color: theme.palette.common.white,
                    width: theme.spacing(10),
                    height: theme.spacing(10),
                  }}
                />

                <Typography
                  variant="h3"
                  sx={{
                    color: theme.palette.common.white,
                    fontSize: '1.4375rem !important',
                    fontWeight: 700,
                    lineHeight: '1.4 !important',
                  }}
                >
                  Let’s get started
                </Typography>
              </Stack>

              <Stack
                sx={{
                  padding: { xs: theme.spacing(6, 0, 0), md: theme.spacing(6) },
                }}
              >
                <Formik
                  initialValues={INITIAL_PARTNER_WITH_US_FORM_VALUES}
                  validationSchema={PartnerWithUsSchema}
                  validateOnChange={false}
                  onSubmit={handleSubmit}
                >
                  <Form>
                    <div className="form-left">
                      <div>
                        <TextField id="name" name="name" label="*Name" />
                      </div>

                      <div>
                        <TextField
                          id="job_title"
                          name="job_title"
                          label="*Job Title"
                        />
                      </div>

                      <div>
                        <TextField id="email" name="email" label="*Email" />
                      </div>

                      <div>
                        <TextField
                          id="phone_number"
                          name="phone_number"
                          label="*Phone Number"
                        />
                      </div>

                      <div>
                        <TextField
                          id="country"
                          name="country"
                          label="*Country"
                        />
                      </div>

                      <div>
                        <TextField
                          id="company_name"
                          name="company_name"
                          label="*Company Name"
                        />
                      </div>
                    </div>
                    <div className="form-right">
                      <div>
                        <TextField
                          id="number_of_employees"
                          name="number_of_employees"
                          label="*Number of Employees"
                        />
                      </div>

                      <div>
                        <TextField
                          id="industry"
                          name="industry"
                          label="Industry"
                        />
                      </div>

                      <div>
                        <TextField
                          id="message"
                          name="message"
                          label="Questions / Comments"
                          multiline
                        />
                      </div>

                      <LoadingButton
                        type="submit"
                        loading={isLoading}
                        variant="contained"
                      >
                        Send
                      </LoadingButton>
                    </div>
                  </Form>
                </Formik>
              </Stack>
            </Stack>
          </Stack>
        </Content>
      </Container>
    </BackgroundSection>
  );
}

export default PartnerWithUsForm;
